import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import { t } from '../i18n';

const ACCENT = '#3fced1';

/**
 * DSGVO-Consent-Banner: erscheint, solange der Nutzer noch nicht
 * entschieden hat (localStorage 'dc-cookie-consent' leer).
 *
 * "Alle akzeptieren" → GA4 wird sofort geladen.
 * "Nur notwendige"  → es wird NICHTS geladen (strikt Opt-in, siehe
 * analyticsService). Beide Wege blenden das Banner dauerhaft aus.
 *
 * Ohne konfigurierte Measurement-ID gibt es nichts einzuwilligen —
 * dann rendert das Banner gar nicht (z. B. lokale Entwicklung).
 */
const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!analyticsService.measurementId) return;
    setVisible(analyticsService.getConsent() === null);
    // Widerruf über die Cookie-Richtlinien-Seite zeigt das Banner erneut
    return analyticsService.onConsentChange((status) => setVisible(status === null));
  }, []);

  if (!visible) return null;

  const decide = (status: 'granted' | 'denied') => {
    analyticsService.setConsent(status);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label={t('web.component.cookie-consent-banner.cookie-einwilligung')}
      className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-zinc-700 bg-zinc-900/95 p-4 shadow-2xl shadow-black/60 backdrop-blur-md sm:p-5">
        <p className="text-sm font-semibold text-white">{t('web.component.cookie-consent-banner.cookies-statistik')}</p>
        <p className="mt-1 text-xs leading-relaxed text-gray-400 sm:text-sm">
          {t('web.component.cookie-consent-banner.wir-nutzen-google-analytics-um')}{' '}
          <RouterLink
            to="/dartcloud/datenschutz"
            className="font-medium underline underline-offset-2 hover:text-white"
            style={{ color: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.datenschutzerklaerung')}
          </RouterLink>{' '}
          und{' '}
          <RouterLink
            to="/dartcloud/cookie-richtlinie"
            className="font-medium underline underline-offset-2 hover:text-white"
            style={{ color: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.cookie-richtlinie')}
          </RouterLink>
          .
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => decide('denied')}
            className="rounded-xl border border-zinc-700 px-4 py-2 text-sm font-semibold text-gray-300 transition-colors hover:border-zinc-500 hover:text-white"
          >
            {t('web.component.cookie-consent-banner.nur-notwendige')}
          </button>
          <button
            type="button"
            onClick={() => decide('granted')}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-zinc-950 transition-opacity hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {t('web.component.cookie-consent-banner.alle-akzeptieren')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
